import { useParams } from 'react-router-dom'
import { createContext, useEffect, useMemo, useState } from 'react'
import { GetAllowedStationsGql } from '@/graphql/queries/get-allowed-stations.graphql'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { AllowedStationsResult } from '@grower-api-types'
import { showErrorNotification } from '@shared/helpers/notifications/notifications'
import { useNavigation } from '@/hooks/use-navigation'

export type StationContextType = {
  station: AllowedStationsResult
  setStation: (data: AllowedStationsResult) => void
  allowedStations: AllowedStationsResult[]
  welcomeMessage?: string | null | undefined
  isLoading: boolean
  isError: boolean
}

// Initial station object that is used to prevent TypeScript errors when
// the station is not yet set. The context defaults to loading state.
const emptyStation: AllowedStationsResult = {
  isOnHold: false,
  recordNumber: '',
  roles: [],
}

export const StationContext = createContext<StationContextType>({
  station: emptyStation,
  setStation: () => {},
  allowedStations: [],
  welcomeMessage: undefined,
  // Default to loading
  isLoading: true,
  isError: false,
})

export const StationContextProvider = ({ children }: any) => {
  const { data, isFetched, isLoading, isError } = useAuthenticatedGraphQuery({
    queryKey: ['allowedStations'],
    gql: GetAllowedStationsGql,
  })
  const [station, setStation] = useState<AllowedStationsResult>(emptyStation)
  const [allowedStations, setAllowedStations] = useState<AllowedStationsResult[]>([])
  const [welcomeMessage, setWelcomeMessage] = useState<string | null | undefined>(undefined)
  const { stationRecordNumber } = useParams()
  const { navigateToHome } = useNavigation()

  // If the stationRecordNumber is present in the URL and the one in context does not match, set the selected station
  useEffect(() => {
    if (
      allowedStations.length &&
      stationRecordNumber &&
      station.recordNumber !== stationRecordNumber
    ) {
      const selectedStation = allowedStations.find(
        (station) => station.recordNumber === stationRecordNumber
      )

      // This means that the URL has a stationRecordNumber that is not in the allowed stations
      // This can happen if the user manually enters a URL or is sent a link from another user
      if (!selectedStation) {
        showErrorNotification(`Station not found`, { autoClose: true })
        navigateToHome()
        return
      }

      setStation(selectedStation)
    }
  }, [stationRecordNumber, station, allowedStations])

  // Set the allowed stations, default station and welcome message when the data is fetched
  useMemo(() => {
    setAllowedStations(data?.allowedStations?.results || [])
    setStation(data?.allowedStations?.results?.[0] || emptyStation)
    setWelcomeMessage(data?.allowedStations?.welcomeMessage)
  }, [data])

  return (
    <StationContext.Provider
      value={{
        station,
        setStation,
        allowedStations,
        welcomeMessage,
        // Only show loading if the query is loading or has not been fetched yet
        isLoading: isLoading || !isFetched,
        isError,
      }}
    >
      {children}
    </StationContext.Provider>
  )
}
